import { IGeoLocation } from "@shared/interfaces";

import { GeoJsonTypeEnum } from "../enums";

const regexLat = /^(-?[1-8]?\d(?:\.\d{1,18})?|90(?:\.0{1,18})?)$/;
const regexLon = /^(-?(?:1[0-7]|[1-9])?\d(?:\.\d{1,18})?|180(?:\.0{1,18})?)$/;

const isValidCoordinates = (lat: string, lon: string): boolean =>
  isValidLatitude(lat) && isValidLongitude(lon);

const isValidLatitude = (lat: string): boolean => regexLat.test(lat);

const isValidLongitude = (lon: string): boolean => regexLon.test(lon);

const getObject = (type: string, coordinates: any[], radius?: number): any => ({
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      geometry: {
        type,
        coordinates,
      },
      properties: {
        name: type,
        ...(radius != null ? { radius } : {}),
      },
    },
  ],
});

const isValid = (geojson: any): boolean => {
  const supportedTypes = [
    GeoJsonTypeEnum.POINT,
    GeoJsonTypeEnum.POLYGON,
    GeoJsonTypeEnum.MULTI_POLYGON,
  ];
  const areValidCoordinates = (coordinates: any): boolean => {
    if (Array.isArray(coordinates)) {
      return coordinates.every((coord) =>
        Array.isArray(coord) ? areValidCoordinates(coord) : typeof coord === "number",
      );
    }

    return false;
  };

  if (geojson?.type === "FeatureCollection" && geojson.features?.length) {
    const featureTypes = geojson.features.map((feature: any) => feature.geometry?.type);

    if (!featureTypes.every((type) => supportedTypes.includes(type))) {
      return false;
    }

    const hasPoint = featureTypes.includes(GeoJsonTypeEnum.POINT);
    const hasPolygon = featureTypes.some((type) => type === GeoJsonTypeEnum.POLYGON);
    const hasMultiPolygon = featureTypes.some((type) => type === GeoJsonTypeEnum.MULTI_POLYGON);

    if (hasPoint && (hasPolygon || hasMultiPolygon)) {
      return false;
    }

    if (
      !geojson.features.every((feature: any) => areValidCoordinates(feature.geometry?.coordinates))
    ) {
      return false;
    }

    return true;
  }

  if (geojson?.geometry?.type) {
    const type = geojson.geometry.type;

    if (!supportedTypes.includes(type)) {
      return false;
    }

    return true;
  }

  return false;
};

const hasMultiPolygonWithOnly1SetOfCoordinates = (geojson: IGeoLocation): boolean =>
  geojson?.features?.some(
    (feature) =>
      feature.geometry?.type === GeoJsonTypeEnum.MULTI_POLYGON &&
      feature.geometry?.coordinates?.length === 1,
  );

const isValidPolygonOrMultiPolygon = (geojson: IGeoLocation): boolean =>
  isValid(geojson) &&
  geojson.features?.length &&
  [GeoJsonTypeEnum.POLYGON, GeoJsonTypeEnum.MULTI_POLYGON].includes(
    geojson.features[0].geometry?.type,
  );

const isPolygon = (geojson: IGeoLocation): boolean =>
  geojson?.features?.length && geojson.features[0].geometry?.type === GeoJsonTypeEnum.POLYGON;

const isPoint = (geojson: IGeoLocation): boolean =>
  geojson?.features[0]?.geometry?.type === GeoJsonTypeEnum.POINT &&
  !!geojson?.features[0]?.geometry?.coordinates?.length;

const wgs84 = {
  RADIUS: 6371008.8,
};

function rad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * This method calculates the area of a geodetic ring in square meters.
 * This method is based on the algorithm described in:
 * Robert G. Chamberlain and William H. Duquette, "Some Algorithms for
 * Polygons on a Sphere", JPL Publication 07-03.
 *
 * @param coords - Coordinates of the ring.
 * @returns The area of the ring in square meters.
 */
function ringArea(coords: number[][]): number {
  let area = 0;
  const coordsLength = coords.length;

  if (coordsLength > 2) {
    for (let i = 0; i < coordsLength; i++) {
      let lowerIndex, middleIndex, upperIndex;

      if (i === coordsLength - 2) {
        lowerIndex = coordsLength - 2;
        middleIndex = coordsLength - 1;
        upperIndex = 0;
      } else if (i === coordsLength - 1) {
        lowerIndex = coordsLength - 1;
        middleIndex = 0;
        upperIndex = 1;
      } else {
        lowerIndex = i;
        middleIndex = i + 1;
        upperIndex = i + 2;
      }

      const p1 = coords[lowerIndex];
      const p2 = coords[middleIndex];
      const p3 = coords[upperIndex];

      area += (rad(p3[0]) - rad(p1[0])) * Math.sin(rad(p2[1]));
    }

    area = (area * wgs84.RADIUS * wgs84.RADIUS) / 2;
  }

  return area;
}

function polygonArea(coords: number[][][]): number {
  let area = 0;

  if (coords && coords.length > 0) {
    area += Math.abs(ringArea(coords[0]));
    for (let i = 1; i < coords.length; i++) {
      area -= Math.abs(ringArea(coords[i]));
    }
  }

  return area;
}
/**
 * This method calculates the total area of a polygon or multipolygon
 * based on a GeoJSON object in square meters.
 *
 * @param geojson - GeoJSON object representing a polygon or multipolygon.
 * @returns The total area in square meters.
 */
const geometry = (geojson: any): number => {
  let area = 0;

  switch (geojson.type) {
    case "Polygon":
      return polygonArea(geojson.coordinates);
    case "MultiPolygon":
      for (let i = 0; i < geojson.coordinates.length; i++) {
        area += polygonArea(geojson.coordinates[i]);
      }

      return area;
    case "Point":
    case "MultiPoint":
    case "LineString":
    case "MultiLineString":
      return 0;
    case "GeometryCollection":
      for (let i = 0; i < geojson.geometries.length; i++) {
        area += geometry(geojson.geometries[i]);
      }

      return area;
  }

  return area;
};

/**
 * Determines the geometry type of a GeoJSON object.
 *
 * @param geojson - The GeoJSON object to evaluate.
 * @returns The geometry type as a GeoJsonTypeEnum, or null if not valid.
 */
const determineGeometryType = (geojson: any): GeoJsonTypeEnum | null => {
  const features = geojson?.features || [];

  if (features.length === 0) {
    return null;
  }

  const geometryType = geojson.geometry?.type || features[0]?.geometry?.type;

  if (geometryType === GeoJsonTypeEnum.POINT) {
    const allPoints = features.every((feature) => feature.geometry?.type === GeoJsonTypeEnum.POINT);

    return allPoints && features.length > 1 ? GeoJsonTypeEnum.MULTI_POINT : GeoJsonTypeEnum.POINT;
  } else if (geometryType === GeoJsonTypeEnum.POLYGON) {
    const allPolygons = features.every(
      (feature) => feature.geometry?.type === GeoJsonTypeEnum.POLYGON,
    );

    return allPolygons && features.length > 1
      ? GeoJsonTypeEnum.MULTI_POLYGON
      : GeoJsonTypeEnum.POLYGON;
  } else if (geometryType === GeoJsonTypeEnum.MULTI_POLYGON) {
    const allMultiPolygons = features.every(
      (feature) => feature.geometry?.type === GeoJsonTypeEnum.MULTI_POLYGON,
    );

    return allMultiPolygons ? GeoJsonTypeEnum.MULTI_POLYGON : null;
  }

  return null;
};

const convertGeometryCollectionToFeatureCollection = (geojson: any): any => {
  if (
    geojson?.type === "FeatureCollection" &&
    geojson.features?.length &&
    geojson.features.some((feature) => feature.geometry?.type === "GeometryCollection")
  ) {
    const feature = geojson.features[0];
    const newFeatures = feature.geometry.geometries.map((geometry: any) => ({
      type: "Feature",
      properties: { ...feature.properties },
      geometry: geometry,
    }));

    return {
      type: "FeatureCollection",
      features: newFeatures,
    };
  }

  return geojson;
};

const GeoJsonError = {
  UNSUPPORTED_GEOMETRY_TYPE: $localize`:@@error.utils.unsupportedgeometrytypes:The location's geo data contains unsupported geometry types. Supported types: Point, Polygon, MultiPolygon`,
  UNSUPPORTED_GEOMETRY_TYPE_COMBINATION: $localize`:@@error.utils.unsupportedgeometrytypes-1:The location's geo data contains unsupported combination of geometry types. Can only have one Point or one / multiple Polygons / MultiPolygons`,
  UNSUPPORTED_DATA_STRUCTURE: $localize`:@@error.utils.unsupporteddatastructure:The location's geo data contains unsupported data structure. Please contact support`,
  DEFAULT: $localize`:@@shared.filenotsupported:File not supported`,
};

const getGeoJsonError = (geojson: any): string => {
  const supportedTypes = [
    GeoJsonTypeEnum.POINT,
    GeoJsonTypeEnum.POLYGON,
    GeoJsonTypeEnum.MULTI_POLYGON,
  ];

  if (geojson?.type === "GeometryCollection") {
    return GeoJsonError.UNSUPPORTED_GEOMETRY_TYPE;
  }

  if (geojson?.type === "FeatureCollection" && geojson.features?.length) {
    const types = geojson.features.map((feature: any) => feature.geometry?.type);

    if (!types.every((type) => supportedTypes.includes(type || ""))) {
      return GeoJsonError.UNSUPPORTED_GEOMETRY_TYPE;
    }

    const uniqueTypes = Array.from(new Set(types));

    if (
      uniqueTypes.includes(GeoJsonTypeEnum.POINT) &&
      (uniqueTypes.includes(GeoJsonTypeEnum.POLYGON) ||
        uniqueTypes.includes(GeoJsonTypeEnum.MULTI_POLYGON))
    ) {
      return GeoJsonError.UNSUPPORTED_GEOMETRY_TYPE_COMBINATION;
    }
  }

  if (geojson?.geometry?.type) {
    const type = geojson.geometry.type;

    if (!supportedTypes.includes(type)) {
      return GeoJsonError.UNSUPPORTED_GEOMETRY_TYPE;
    }
  }

  return GeoJsonError.DEFAULT;
};

const getAreaInHa = (value: number): number => {
  return value / 10_000;
};

/**
 * Calculates the circle area for a single Point feature with a radius property.
 * The radius is assumed to be in meters, so the returned area is in square meters.
 *
 * @param geojson - A GeoJSON FeatureCollection containing exactly one Point feature.
 * @returns The calculated area in square meters, or 0 if the feature is not a point or has no radius.
 */
const calculateCircleAreaForSinglePoint = (geojson: any, radiusInMeters?: number): number => {
  if (radiusInMeters) {
    return Math.PI * Math.pow(radiusInMeters, 2);
  }
  // Ensure there's at least one feature
  if (!geojson?.features?.length) {
    return 0;
  }

  const feature = geojson.features[0];

  // Check if it's actually a Point
  if (feature.geometry?.type !== "Point") {
    return 0;
  }

  // Check for a numeric radius
  const radius = feature.properties?.radius;

  if (typeof radius !== "number") {
    return 0;
  }

  // Compute circle area: πr²
  return Math.PI * Math.pow(radius, 2);
};

/**
 * This method was generated with the help of IA --> ChatGpt Model o1.
 *
 * Returns a LatLngBounds object that encompasses a circle of the given radius around
 * the specified center latitude/longitude.
 *
 * Note: This is an approximation that treats 1 degree of latitude as ~111,111 m,
 * and adjusts longitude degrees by the cosine of the latitude.
 * It works well for moderate distances but won't perfectly account for all ellipsoidal effects.
 *
 * @param centerLat - The center latitude in decimal degrees
 * @param centerLng - The center longitude in decimal degrees
 * @param radiusInMeters - The radius in meters
 * @returns A google.maps.LatLngBounds covering the approximate circle.
 */
const getBoundsFromRadius = (
  centerLat: number,
  centerLng: number,
  radiusInMeters: number,
): google.maps.LatLngBounds => {
  // 1) Convert the radius (meters) to degrees of latitude (~111,111 m per degree)
  const latDiff = radiusInMeters / 111_111;

  // 2) Adjust for longitude degrees using the cosine of the center latitude
  const lngDiff = radiusInMeters / (111_111 * Math.cos((centerLat * Math.PI) / 180));

  // 3) Create the SW and NE corners of the bounding box
  const sw = new google.maps.LatLng(centerLat - latDiff, centerLng - lngDiff);
  const ne = new google.maps.LatLng(centerLat + latDiff, centerLng + lngDiff);

  // 4) Return a LatLngBounds that covers that box
  return new google.maps.LatLngBounds(sw, ne);
};

/**
 * Generated with AI assistance. Reviewed and modified for accuracy.
 * Checks if all given points are collinear (lie on the same straight line).
 *
 * @param points - An array of points, each with x and y coordinates.
 * @param epsilon - A small tolerance value to handle floating-point precision errors (default: 1e-12).
 * @returns `true` if all points are collinear, otherwise `false`.
 */
const areAllPointsColinear = (points: { x: number; y: number }[], epsilon = 1e-12): boolean => {
  // If there are 2 or fewer points, they are always considered collinear.
  if (points.length <= 2) {
    return true;
  }

  // Extract the first two points as the reference line.
  const [p0, p1, ...rest] = points;

  // Check if every other point is collinear with the first two points.
  for (let i = 0; i < rest.length; i++) {
    if (!areThreePointsColinear(p0, p1, rest[i], epsilon)) {
      return false; // If any point is not collinear, return false.
    }
  }

  return true; // If all points are collinear, return true.
};

/**
 * Generated with AI assistance. Reviewed and modified for accuracy.
 * Determines if three given points are collinear.
 *
 * @param A - The first point.
 * @param B - The second point.
 * @param C - The third point.
 * @param epsilon - A small tolerance value to handle floating-point precision errors (default: 1e-12).
 * @returns `true` if the three points are collinear, otherwise `false`.
 */
const areThreePointsColinear = (
  A: { x: number; y: number },
  B: { x: number; y: number },
  C: { x: number; y: number },
  epsilon = 1e-12,
): boolean => {
  // Compute the cross product of vectors AB and AC.
  // If the result is close to zero, the points are collinear.
  const cross = (B.x - A.x) * (C.y - A.y) - (B.y - A.y) * (C.x - A.x);

  return Math.abs(cross) < epsilon;
};

/**
 * This method was generated with the help of IA --> ChatGpt Model 4o
 * Normalizes a GeoJSON FeatureCollection by ensuring consistent and valid MultiPolygon structures.
 *
 * This function performs the following operations:
 * 1. Removes features with null or missing geometry.
 * 2. Flattens overly nested MultiPolygon coordinate arrays (e.g., [[[[...]]]]).
 * 3. Converts Polygon geometries to MultiPolygon to ensure a uniform structure.
 * 4. Wraps MultiPolygon coordinate arrays to ensure exactly 3 levels of depth:
 *    MultiPolygon → [Polygon[]] → [LinearRing[]] → [Position[]]
 *
 * These transformations help avoid rendering or parsing issues
 * in mapping libraries such as Google Maps.
 *
 * @param geojson - The input GeoJSON object to normalize.
 * @returns The normalized GeoJSON object.
 */
const normalizeGeoJsonMultiPolygons = (geojson: any): any => {
  if (!geojson || geojson.type !== "FeatureCollection") {
    return geojson;
  }

  // 1. Remove features with null geometry
  geojson.features = geojson.features.filter((feature) => !!feature.geometry);

  for (const feature of geojson.features) {
    const geometry = feature.geometry;

    if (!geometry) {
      continue;
    }

    let coords = geometry.coordinates;

    // 2. Flatten excessive nesting in MultiPolygon coordinates
    while (
      Array.isArray(coords) &&
      coords.length === 1 &&
      Array.isArray(coords[0]) &&
      coords[0].length === 1 &&
      Array.isArray(coords[0][0]) &&
      Array.isArray(coords[0][0][0])
    ) {
      coords = coords[0];
    }

    // 3. Convert Polygon to MultiPolygon
    if (geometry.type === "Polygon") {
      geometry.type = "MultiPolygon";
      coords = [coords];
    }

    // 4. Ensure proper 3-level MultiPolygon nesting
    if (
      geometry.type === "MultiPolygon" &&
      Array.isArray(coords) &&
      coords.length &&
      Array.isArray(coords[0]) &&
      Array.isArray(coords[0][0]) &&
      !Array.isArray(coords[0][0][0])
    ) {
      coords = [[coords[0]]];
    }

    geometry.coordinates = coords;
  }

  return geojson;
};

/**
 * This method was generated with the help of IA --> ChatGpt Model 4o
 * Checks whether a given GeoJSON object requires normalization before further processing.
 *
 * Normalization may be required if:
 * - One or more features have a null geometry.
 * - A MultiPolygon has excessive nesting (e.g., [[[[...]]]]) which breaks rendering.
 * - A Polygon is used where a MultiPolygon is expected, to ensure consistent structure.
 *
 * @param geojson - The GeoJSON object to check.
 * @returns `true` if normalization is needed; otherwise, `false`.
 */
const needsNormalization = (geojson: any): boolean => {
  if (!geojson || geojson.type !== "FeatureCollection" || !Array.isArray(geojson.features)) {
    return false;
  }

  for (const feature of geojson.features) {
    const geometry = feature.geometry;

    if (!geometry) {
      return true; // Null geometry
    }

    const coords = geometry.coordinates;

    // Detect excessive nesting in MultiPolygon coordinates
    if (
      geometry.type === "MultiPolygon" &&
      Array.isArray(coords) &&
      coords.length === 1 &&
      Array.isArray(coords[0]) &&
      coords[0].length === 1 &&
      Array.isArray(coords[0][0]) &&
      Array.isArray(coords[0][0][0])
    ) {
      return true;
    }
  }

  return false;
};

/**
 * This method was generated with the help of IA --> ChatGpt Model 4o
 * Calculates the geodesic distance between two points on Earth specified by their
 * latitude and longitude using the Haversine formula.
 *
 * @param lat1 - Latitude of the first point in decimal degrees.
 * @param lon1 - Longitude of the first point in decimal degrees.
 * @param lat2 - Latitude of the second point in decimal degrees.
 * @param lon2 - Longitude of the second point in decimal degrees.
 * @returns The distance between the two points in meters.
 *
 * This method assumes a spherical Earth with a radius of 6,371,000 meters.
 * It is accurate for most use cases where high-precision geodesic measurement is not critical.
 */
const getDistanceInMeters = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6_371_000; // Earth's radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

export const GeoJSONUtils = {
  isValidCoordinates,
  isValidLatitude,
  isValidLongitude,
  getObject,
  isValid,
  isValidPolygonOrMultiPolygon,
  isPolygon,
  isPoint,
  geometry,
  determineGeometryType,
  getGeoJsonError,
  convertGeometryCollectionToFeatureCollection,
  calculateCircleAreaForSinglePoint,
  getBoundsFromRadius,
  getAreaInHa,
  hasMultiPolygonWithOnly1SetOfCoordinates,
  needsNormalization,
  normalizeGeoJsonMultiPolygons,
  areAllPointsColinear,
  getDistanceInMeters,
};
